/** Reading a chat table artifact's body into a grid the card can render.
 *
 *  A mirror of the JSON half of `agent_runtime/documents/normalize.py`, which
 *  is the authority: it decides what `GET /artifacts/{id}/export` will render,
 *  and this decides what the card shows and whether it offers the Export menu.
 *  When the two disagree the user is either shown a table they cannot download
 *  or offered a download of something they were never shown -- so the shapes
 *  below, and their refusals, are deliberately the server's.
 *
 *  The card previously accepted a top-level JSON array and nothing else, which
 *  meant `{header, rows}`, CSV and markdown bodies rendered "No tabular data."
 *  with no Export button, for artifacts the route answers 200 for.
 *
 *  CSV and markdown are NOT parsed here, only recognised. A body that satisfies
 *  the guards `_from_csv` / `_from_markdown` apply is reported `opaque`: the
 *  card cannot draw it but the server can render it, so Export stays offered.
 *  Re-implementing a CSV reader client-side would be a second dialect to keep
 *  in step for the sake of a preview. `unexportable` covers what the route
 *  really does 409 on -- valid JSON in no table shape, and prose that is
 *  neither -- so the menu is hidden close to exactly when it would fail.
 */

/** What a spreadsheet cell can hold. `bool` rides along as the server's
 *  `_SCALAR_CELL` does. */
type Scalar = string | number | boolean | null;

export type ArtifactBody =
  | { status: "table"; headers: string[]; rows: unknown[][] }
  /** Not JSON. The server's CSV and markdown readers may still export it. */
  | { status: "opaque" }
  /** JSON, but no table shape in it — the body the route 409s on. */
  | { status: "unexportable" };

/** Could the server's markdown or CSV reader make a table of this?
 *
 *  A precondition check, not a parser. It mirrors the guards that decide
 *  whether `_from_markdown` / `_from_csv` return a table, so the Export menu is
 *  offered on the bodies the route renders and withheld on the prose it 409s
 *  on — without keeping a second CSV dialect in step for the sake of a preview
 *  the card does not draw anyway.
 */
function looksTabular(text: string): boolean {
  const lines = text.split(/\r?\n/);
  return looksLikeMarkdownTable(lines) || looksLikeCsv(lines);
}

const MD_SEPARATOR_CELL = /^:?-+:?$/;

function splitPipeRow(line: string): string[] {
  return line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
}

/** Mirrors `_from_markdown`: a row, a separator row under it, and a data row. */
function looksLikeMarkdownTable(lines: string[]): boolean {
  for (let i = 0; i < lines.length - 2; i++) {
    if (!lines[i].includes("|") || !lines[i + 1].includes("|")) continue;
    const separator = splitPipeRow(lines[i + 1]);
    if (!separator.length || !separator.every((c) => MD_SEPARATOR_CELL.test(c))) {
      continue;
    }
    if (splitPipeRow(lines[i]).length && lines[i + 2].includes("|")) return true;
  }
  return false;
}

/** Header plus at least one data row, matching `_MIN_CSV_ROWS`.
 *
 *  Three, not two: two lines of comma-separated text are equally well two
 *  sentences or two lines of code, and both are rectangular. */
const MIN_CSV_ROWS = 3;

/** Mirrors `_from_csv`'s guards: at least three non-blank rows, at least two
 *  columns, every row the same width.
 *
 *  Splits on commas where the server runs a real CSV reader, so a quoted field
 *  containing a comma counts as two here. That only matters for a body whose
 *  widths then disagree, and the cost is the menu being withheld from a body
 *  the server would have rendered — the same answer this code gave for every
 *  CSV before, rather than a new failure. */
function looksLikeCsv(lines: string[]): boolean {
  const rows = lines.filter((l) => l.trim()).map((l) => l.split(","));
  if (rows.length < MIN_CSV_ROWS) return false;
  const width = rows[0].length;
  return width >= 2 && rows.every((r) => r.length === width);
}

/** Header plus at least one data row, matching `_MIN_ROW_ARRAY_ROWS`. */
const MIN_ROW_ARRAY_ROWS = 2;

const UNEXPORTABLE: ArtifactBody = { status: "unexportable" };

function isScalar(value: unknown): value is Scalar {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

/** Mirrors `_cell`: numbers stay numbers — a spreadsheet whose figures are
 *  strings cannot be summed, which is the reason Excel was asked for. */
function cell(value: unknown): unknown {
  if (value === null || value === undefined) return "";
  if (isScalar(value)) return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Mirrors `_is_grid_row`: a list of scalars, or a bare scalar for a table one
 *  column wide. A dict row would otherwise become a single JSON-blob cell. */
function isGridRow(row: unknown): boolean {
  if (Array.isArray(row)) return row.every(isScalar);
  return isScalar(row);
}

function rowCells(row: unknown): unknown[] {
  return Array.isArray(row) ? row.map(cell) : [cell(row)];
}

/** Mirrors `_rectangular`: pad to a common width, widening the header rather
 *  than dropping cells — `docx.py` truncates a row to the header width, so an
 *  over-long row would lose data silently in that one format. */
function rectangular(header: unknown[], rows: unknown[][]): ArtifactBody {
  const width = Math.max(header.length, ...rows.map((r) => r.length));
  const headers = [
    ...header.map((h) => String(cell(h))),
    ...Array<string>(width - header.length).fill(""),
  ];
  return {
    status: "table",
    headers,
    rows: rows.map((r) => [...r, ...Array(width - r.length).fill("")]),
  };
}

/** Mirrors `_from_records`: the header is the union of every row's keys.
 *
 *  Not `Object.keys(rows[0])`: the body is a model's transcription of a
 *  truncated tool result, and a record that omits a key it supplies two rows
 *  later is exactly what that produces. */
function fromRecords(records: unknown[]): ArtifactBody | null {
  if (!records.length) return null;
  if (!records.every((r) => r !== null && typeof r === "object" && !Array.isArray(r))) {
    return null;
  }
  const rows = records as Record<string, unknown>[];
  const header: string[] = [];
  for (const record of rows) {
    for (const key of Object.keys(record)) {
      if (!header.includes(key)) header.push(key);
    }
  }
  if (!header.length) return null;
  return rectangular(
    header,
    rows.map((record) => header.map((key) => cell(record[key]))),
  );
}

/** Mirrors `_from_rows`: array of arrays, first row is the header.
 *
 *  The guards are what keeps this from manufacturing tables: every element a
 *  list, every cell a scalar (rows of rows is a tree, and `cell` would flatten
 *  its inner lists into a header of JSON blobs), and a non-empty header. */
function fromRows(rows: unknown[]): ArtifactBody | null {
  if (rows.length < MIN_ROW_ARRAY_ROWS) return null;
  if (!rows.every((row) => Array.isArray(row))) return null;
  if (!rows.every((row) => (row as unknown[]).every(isScalar))) return null;
  const header = rows[0] as unknown[];
  if (!header.length) return null;
  return rectangular(header, (rows.slice(1) as unknown[][]).map(rowCells));
}

export function readArtifactBody(text: string): ArtifactBody {
  if (!text.trim()) return UNEXPORTABLE;

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Not JSON. Valid JSON that is not a table shape is not secretly CSV or
    // markdown, so only this branch reaches the server's other two readers —
    // and only a body that could satisfy one of them is `opaque`. Prose is
    // still `unexportable`, which is what the route answers for it.
    return looksTabular(text) ? { status: "opaque" } : UNEXPORTABLE;
  }

  if (Array.isArray(parsed)) {
    return fromRecords(parsed) ?? fromRows(parsed) ?? UNEXPORTABLE;
  }

  if (parsed !== null && typeof parsed === "object") {
    const { header, rows } = parsed as { header?: unknown; rows?: unknown };
    if (Array.isArray(header) && Array.isArray(rows) && rows.length) {
      if (!rows.every(isGridRow)) return UNEXPORTABLE;
      return rectangular(header, rows.map(rowCells));
    }
  }

  return UNEXPORTABLE;
}
